import React from "react";
import { TableBody, TableCell, TableRow } from "@mui/material";
import TruncatedText from "../TruncatedText";
import { AnalysisDriverType } from "../../types/types";
import { EditedMetrics, RowSegment } from "./DriverMetricsTableBody";
import { styled } from "@mui/material/styles";
import colors from "assets/theme-dark/base/colors";
import { DriverCombinedRow, DriverTableSegment } from "../../utils/driverTableUtils";
import DriverMetricCell from "./DriverMetricTableCell";
import { useHeadroomContext } from "../../context/HeadRoomProvider";

interface DriverMetricsRowProps {
  rows: RowSegment[][] | DriverCombinedRow[];
  segments?: DriverTableSegment[];
  isManualOnly?: boolean;
  isTwoSameMethod?: boolean;
  hasOneDataAndOneManualSegment?: boolean;
  driverMode: AnalysisDriverType;
  editedPercentiles: EditedMetrics;
  setEditedPercentiles: React.Dispatch<React.SetStateAction<EditedMetrics>>;
  editingCell: { uuid: string; field: string } | null;
  setEditingCell: (cell: { uuid: string; field: string } | null) => void;
}

const DriverMetricsRow: React.FC<DriverMetricsRowProps> = ({
  rows,
  segments,
  isManualOnly,
  isTwoSameMethod,
  hasOneDataAndOneManualSegment,
  driverMode,
  editedPercentiles,
  setEditedPercentiles,
  editingCell,
  setEditingCell,
}) => {
  const { headroomDriver } = useHeadroomContext();

  const medianField = headroomDriver === "driver1" ? "driver1Median" : "driver2Median";
  const avgField = headroomDriver === "driver1" ? "driver1Avg" : "driver2Avg";
  const percentileField = headroomDriver === "driver1" ? "driver1Percentile" : "driver2Percentile";

  if (hasOneDataAndOneManualSegment) {
    return (
      <TableBody>
        {(rows as DriverCombinedRow[]).map((row, rowIndex) => {
          const uuid = row.data?.FxUniqueRowUuid || `row-${rowIndex}`;
          return (
            <TableRow key={uuid}>
              <RegularTableCell align="center">{row.manualRange || ""}</RegularTableCell>
              <RegularTableCell align="center">
                <TruncatedText text={row.dataValue || ""} maxLength={20} />
              </RegularTableCell>
              <DriverMetricCell
                uuid={uuid}
                field={medianField}
                value={row.data?.[medianField]}
                driverMode={driverMode}
                editedPercentiles={editedPercentiles}
                setEditedPercentiles={setEditedPercentiles}
                isEditing={editingCell?.uuid === uuid && editingCell?.field === medianField}
                setEditingCell={setEditingCell}
              />
              <DriverMetricCell
                uuid={uuid}
                field={avgField}
                value={row.data?.[avgField]}
                driverMode={driverMode}
                editedPercentiles={editedPercentiles}
                setEditedPercentiles={setEditedPercentiles}
                isEditing={editingCell?.uuid === uuid && editingCell?.field === avgField}
                setEditingCell={setEditingCell}
              />
              <DriverMetricCell
                uuid={uuid}
                field={percentileField}
                value={row.data?.[percentileField]}
                driverMode={driverMode}
                editedPercentiles={editedPercentiles}
                setEditedPercentiles={setEditedPercentiles}
                isEditing={editingCell?.uuid === uuid && editingCell?.field === percentileField}
                setEditingCell={setEditingCell}
              />
            </TableRow>
          );
        })}
      </TableBody>
    );
  }

  return (
    <TableBody>
      {(rows as RowSegment[][]).map((rowSegments, rowIndex) => {
        const rangeData = rowSegments.find((r) => r.data);
        const segment1Data = rowSegments[0].data;
        const segment2Data = rowSegments[1]?.data;
        const uuid =
          rangeData?.data?.FxUniqueRowUuid ||
          segment1Data?.FxUniqueRowUuid ||
          segment2Data?.FxUniqueRowUuid ||
          `row-${rowIndex}`;

        return (
          <TableRow key={uuid}>
            {isManualOnly ? (
              <>
                <RegularTableCell align="center">Manual</RegularTableCell>
                <RegularTableCell align="center">
                  <TruncatedText text={rowSegments[0].segmentValue || ""} maxLength={20} />
                </RegularTableCell>
              </>
            ) : isTwoSameMethod && segments ? (
              <>
                <RegularTableCell align="center" isEmpty={!(rowSegments[0].segmentValue || rowSegments[0].range)}>
                  <TruncatedText
                    text={
                      segments[0].method === "DATA" ? rowSegments[0].segmentValue || "" : rowSegments[0].range || ""
                    }
                    maxLength={20}
                  />
                </RegularTableCell>
                <RegularTableCell align="center" isEmpty={!(rowSegments[1]?.segmentValue || rowSegments[1]?.range)}>
                  <TruncatedText
                    text={
                      segments[1].method === "DATA" ? rowSegments[1]?.segmentValue || "" : rowSegments[1]?.range || ""
                    }
                    maxLength={20}
                  />
                </RegularTableCell>
              </>
            ) : (
              rowSegments.map((range, index) => (
                <React.Fragment key={`range-${index}`}>
                  <RegularTableCell align="center">{range.range || ""}</RegularTableCell>
                  <RegularTableCell align="center">
                    <TruncatedText text={range.segmentValue || ""} maxLength={20} />
                  </RegularTableCell>
                </React.Fragment>
              ))
            )}
            <DriverMetricCell
              uuid={uuid}
              field={medianField}
              value={rangeData?.data?.[medianField] || segment1Data?.[medianField] || segment2Data?.[medianField]}
              driverMode={driverMode}
              editedPercentiles={editedPercentiles}
              setEditedPercentiles={setEditedPercentiles}
              isEditing={editingCell?.uuid === uuid && editingCell?.field === medianField}
              setEditingCell={setEditingCell}
            />
            <DriverMetricCell
              uuid={uuid}
              field={avgField}
              value={rangeData?.data?.[avgField] || segment1Data?.[avgField] || segment2Data?.[avgField]}
              driverMode={driverMode}
              editedPercentiles={editedPercentiles}
              setEditedPercentiles={setEditedPercentiles}
              isEditing={editingCell?.uuid === uuid && editingCell?.field === avgField}
              setEditingCell={setEditingCell}
            />
            <DriverMetricCell
              uuid={uuid}
              field={percentileField}
              value={
                rangeData?.data?.[percentileField] || segment1Data?.[percentileField] || segment2Data?.[percentileField]
              }
              driverMode={driverMode}
              editedPercentiles={editedPercentiles}
              setEditedPercentiles={setEditedPercentiles}
              isEditing={editingCell?.uuid === uuid && editingCell?.field === percentileField}
              setEditingCell={setEditingCell}
            />
          </TableRow>
        );
      })}
    </TableBody>
  );
};

const RegularTableCell = styled(TableCell)<{ isEmpty?: boolean }>(({ theme, isEmpty }) => ({
  border: `1px solid ${colors.grey[300]}`,
  padding: theme.spacing(1.5),
  fontSize: "0.75rem",
  color: colors.text.primary,
  backgroundColor: isEmpty ? colors.grey[100] : colors.background.default,
  whiteSpace: "nowrap",
  lineHeight: "1rem",
}));

export default DriverMetricsRow;
